import { parse } from 'media-typer';
import {
  CONNECT_PROTO_CONTENT_TYPE,
  CONNECT_UNARY_PROTO_CONTENT_TYPE,
  GRPC_WEB_CONTENT_TYPE,
  GRPC_WEB_PROTO_CONTENT_TYPE,
  GRPC_WEB_TEXT_CONTENT_TYPE,
  GRPC_WEB_TEXT_PROTO_CONTENT_TYPE,
  NATIVE_HTTP2_GRPC_CONTENT_TYPE,
  NATIVE_HTTP2_GRPC_PROTO_CONTENT_TYPE,
} from './grpc-web-detect';

export const NO_MIME_TYPE = 'NO_MIME_TYPE';

export const GRPC_WEB_RESPONSE_CONTENT_TYPES = [
  GRPC_WEB_CONTENT_TYPE,
  GRPC_WEB_PROTO_CONTENT_TYPE,
  GRPC_WEB_TEXT_CONTENT_TYPE,
  GRPC_WEB_TEXT_PROTO_CONTENT_TYPE,
  NATIVE_HTTP2_GRPC_CONTENT_TYPE,
  NATIVE_HTTP2_GRPC_PROTO_CONTENT_TYPE,
  CONNECT_PROTO_CONTENT_TYPE,
  CONNECT_UNARY_PROTO_CONTENT_TYPE,
] as const;

export type DiscoveryContentTypeChannel = 'crawler' | 'torchTracking';

export type DiscoveryContentTypeOrigin = 'DEFAULT' | 'CUSTOM';

export type DiscoveryContentTypeDefinition = {
  mimeType: string;
  description: string;
  defaultCrawlerEnabled: boolean;
  defaultTorchTrackingEnabled: boolean;
};

export type DiscoveryContentTypeConfig = {
  mimeType: string;
  description?: string | null;
  origin: DiscoveryContentTypeOrigin;
  crawlerEnabled: boolean;
  torchTrackingEnabled: boolean;
};

export type StoredDiscoveryContentTypeConfig = {
  mimeType: string;
  description?: string | null;
  origin?: DiscoveryContentTypeOrigin | null;
  crawlerEnabled?: boolean | null;
  torchTrackingEnabled?: boolean | null;
  portalTrackingEnabled?: boolean | null;
};

export const DISCOVERY_CONTENT_TYPE_DEFINITIONS: readonly DiscoveryContentTypeDefinition[] =
  [
    {
      mimeType: 'application/json',
      description: 'REST / JSON API responses',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'application/xml',
      description: 'XML API responses',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'text/html',
      description: 'Web pages / HTML (route discovery)',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'text/xml',
      description: 'XML documents',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'application/graphql',
      description: 'GraphQL responses',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'application/x-www-form-urlencoded',
      description: 'Form-encoded responses',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'text/event-stream',
      description: 'Server-sent events',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'text/x-script',
      description: 'Script responses',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'application/javascript',
      description: 'JavaScript bundles',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'text/javascript',
      description: 'JavaScript modules',
      defaultCrawlerEnabled: true,
      defaultTorchTrackingEnabled: true,
    },
    {
      mimeType: 'text/csv',
      description: 'CSV downloads',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: 'application/octet-stream',
      description: 'Binary / opaque payloads',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: GRPC_WEB_CONTENT_TYPE,
      description: 'gRPC-web binary responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: GRPC_WEB_PROTO_CONTENT_TYPE,
      description: 'gRPC-web protobuf responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: GRPC_WEB_TEXT_CONTENT_TYPE,
      description: 'gRPC-web text (base64) responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: GRPC_WEB_TEXT_PROTO_CONTENT_TYPE,
      description: 'gRPC-web text protobuf responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: NATIVE_HTTP2_GRPC_CONTENT_TYPE,
      description: 'Native HTTP/2 gRPC responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: NATIVE_HTTP2_GRPC_PROTO_CONTENT_TYPE,
      description: 'Native HTTP/2 gRPC protobuf responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: CONNECT_PROTO_CONTENT_TYPE,
      description: 'Connect protobuf streaming responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: CONNECT_UNARY_PROTO_CONTENT_TYPE,
      description: 'Connect unary protobuf responses',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
    {
      mimeType: NO_MIME_TYPE,
      description: 'Responses with no Content-Type header',
      defaultCrawlerEnabled: false,
      defaultTorchTrackingEnabled: false,
    },
  ];

const DEFINITION_BY_MIME = new Map(
  DISCOVERY_CONTENT_TYPE_DEFINITIONS.map((definition) => [
    normalizeMimeKey(definition.mimeType),
    definition,
  ]),
);

export function normalizeMimeKey(mimeType: string): string {
  return mimeType.trim().toLowerCase();
}

export function isValidDiscoveryContentType(mimeType: string): boolean {
  const trimmed = mimeType.trim();

  if (!trimmed) {
    return false;
  }

  if (trimmed === NO_MIME_TYPE) {
    return true;
  }

  const mediaType = trimmed.split(';')[0].trim();

  try {
    parse(mediaType);

    return true;
  } catch {
    return false;
  }
}

export function definitionToConfig(
  definition: DiscoveryContentTypeDefinition,
): DiscoveryContentTypeConfig {
  return {
    mimeType: definition.mimeType,
    description: definition.description,
    origin: 'DEFAULT',
    crawlerEnabled: definition.defaultCrawlerEnabled,
    torchTrackingEnabled: definition.defaultTorchTrackingEnabled,
  };
}

const readStoredBoolean = (
  value: boolean | null | undefined,
  fallback: boolean,
): boolean => (typeof value === 'boolean' ? value : fallback);

const readTorchTrackingEnabled = (
  stored: StoredDiscoveryContentTypeConfig,
  fallback: boolean,
): boolean => {
  if (typeof stored.torchTrackingEnabled === 'boolean') {
    return stored.torchTrackingEnabled;
  }

  return readStoredBoolean(stored.portalTrackingEnabled, fallback);
};

export function resolveDefaultDiscoveryContentTypes(): DiscoveryContentTypeConfig[] {
  return DISCOVERY_CONTENT_TYPE_DEFINITIONS.map(definitionToConfig);
}

export function resolveDiscoveryContentTypes(
  stored?: readonly StoredDiscoveryContentTypeConfig[] | null,
): DiscoveryContentTypeConfig[] {
  if (!stored || stored.length === 0) {
    return resolveDefaultDiscoveryContentTypes();
  }

  const storedByMime = new Map(
    stored.map((config) => [normalizeMimeKey(config.mimeType), config]),
  );

  const defaultTypeConfigs = DISCOVERY_CONTENT_TYPE_DEFINITIONS.map(
    (definition) => {
      const storedConfig = storedByMime.get(
        normalizeMimeKey(definition.mimeType),
      );

      if (!storedConfig) {
        return definitionToConfig(definition);
      }

      return {
        mimeType: definition.mimeType,
        description: definition.description,
        origin: 'DEFAULT' as const,
        crawlerEnabled: readStoredBoolean(
          storedConfig.crawlerEnabled,
          definition.defaultCrawlerEnabled,
        ),
        torchTrackingEnabled: readTorchTrackingEnabled(
          storedConfig,
          definition.defaultTorchTrackingEnabled,
        ),
      };
    },
  );

  const customConfigs = stored
    .filter((config) => {
      const key = normalizeMimeKey(config.mimeType);

      return !DEFINITION_BY_MIME.has(key);
    })
    .filter((config, index, all) => {
      const key = normalizeMimeKey(config.mimeType);

      return (
        all.findIndex((item) => normalizeMimeKey(item.mimeType) === key) ===
        index
      );
    })
    .map((config) => {
      const description =
        typeof config.description === 'string' ? config.description : undefined;

      return {
        mimeType: config.mimeType.trim(),
        ...(description !== undefined ? { description } : {}),
        origin: 'CUSTOM' as const,
        crawlerEnabled: readStoredBoolean(config.crawlerEnabled, true),
        torchTrackingEnabled: readTorchTrackingEnabled(config, true),
      };
    });

  return [...defaultTypeConfigs, ...customConfigs];
}

export function getCrawlerMimeTypes(
  configs: readonly DiscoveryContentTypeConfig[],
): string[] {
  return configs
    .filter((config) => config.crawlerEnabled)
    .map((config) => config.mimeType);
}

export function getTorchTrackingMimeTypes(
  configs: readonly DiscoveryContentTypeConfig[],
): string[] {
  return configs
    .filter((config) => config.torchTrackingEnabled)
    .map((config) => config.mimeType);
}

export function appendGrpcWebResponseMimeTypes({
  mimeTypes,
  grpcWebSupportEnabled,
}: {
  mimeTypes: readonly string[];
  grpcWebSupportEnabled: boolean;
}): string[] {
  if (!grpcWebSupportEnabled) {
    return [...mimeTypes];
  }

  return [...new Set([...mimeTypes, ...GRPC_WEB_RESPONSE_CONTENT_TYPES])];
}
